/**
 * Shared cap-weight + breadth roll-up without a SectorId (area taxonomy plan).
 *
 * `aggregateSectors` is the GICS wrapper; this module owns the one numeric
 * path so industry tiles cannot drift from sector tiles on the same rows.
 */
import type { AreaAggregate, AreaId, WindowKey } from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';

import { computeBreadth } from './breadth';
import { capWeightedReturn } from './cap-weighted-return';

export interface GroupMember {
  readonly mcap: number | null;
  readonly vol: number | null;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

export interface GroupAggregateDetail<
  T extends GroupMember = GroupMember,
  Id extends string = string,
> {
  readonly id: Id;
  readonly name: string;
  readonly mcap: number;
  readonly ret: number | null;
  readonly memberCount: number;
  readonly advancers: number;
  readonly decliners: number;
  readonly avgVol: number;
  readonly members: readonly T[];
}

/**
 * Distinct ids in first-encounter order, then one scan per id.
 *
 * The previous fold cloned the whole accumulator Map AND the whole member
 * array once per row — quadratic in group count and group size, and ~n Map
 * plus ~n array allocations per call. This allocates one Set plus one array
 * per GROUP instead, which matters because `aggregateSectors` routes through
 * here on /v1/dashboard and /v1/sectors, and the area list has ~111 groups
 * rather than 11.
 *
 * A single mutable-accumulator pass would be cheaper still, but
 * `functional/no-loop-statements` and `functional/immutable-data` are errors
 * across `packages/core/src/**` with exactly one sanctioned exemption
 * (`series-cache.ts`, ADR-0004). This shape is the fastest lint-clean one, and
 * mirrors the `groupBySector` scan-per-id it replaced.
 *
 * Group ORDER is not load-bearing: `aggregateGroups` applies a total sort whose
 * tie-break is the group id. Member order within a group stays row order.
 */
function groupById<T, Id extends string>(
  rows: readonly T[],
  idOf: (row: T) => Id,
): readonly (readonly [Id, readonly T[]])[] {
  return [...new Set(rows.map(idOf))].map(
    (id) => [id, rows.filter((row) => idOf(row) === id)] as const,
  );
}

function mean(values: readonly number[]): number {
  return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
}

function aggregateOne<T extends GroupMember, Id extends string>(
  id: Id,
  name: string,
  members: readonly T[],
  window: WindowKey,
): GroupAggregateDetail<T, Id> {
  const mcap = members.reduce((a, m) => a + (m.mcap ?? 0), 0);
  const breadth = computeBreadth(members, window);
  return {
    id,
    name,
    mcap,
    ret: capWeightedReturn(members, window),
    memberCount: members.length,
    advancers: breadth.advancers,
    decliners: breadth.decliners,
    avgVol: mean(members.map((m) => m.vol).filter((v): v is number => v !== null)),
    members,
  };
}

/** Descending by return, with an UNKNOWN return last in both directions. */
function compareRetDesc(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

export interface GroupAggregateOptions<T, Id extends string> {
  readonly idOf: (row: T) => Id;
  readonly nameOf: (id: Id) => string;
  readonly window: WindowKey;
}

/**
 * Roll rows up into one bucket per group id.
 *
 * An OPTIONS object rather than four positional arguments: `idOf` and `nameOf`
 * are adjacent single-argument functions returning string-ish values, so
 * positionally they are confusable, and the positional form already sat at the
 * `max-params` ceiling of 4 — leaving a newly-public symbol no room to grow
 * without either a breaking signature change or a lint failure.
 */
export function aggregateGroups<T extends GroupMember, Id extends string>(
  rows: readonly T[],
  options: GroupAggregateOptions<T, Id>,
): readonly GroupAggregateDetail<T, Id>[] {
  return groupById(rows, options.idOf)
    .map(([id, members]) => aggregateOne(id, options.nameOf(id), members, options.window))
    .sort((a, b) => compareRetDesc(a.ret, b.ret) || COLLATOR.compare(a.id, b.id));
}

/**
 * Narrow the core detail to the change-controlled wire shape.
 *
 * ONE narrowing shared by both adapters, mirroring `toSectorAggregate`. Two
 * copies — one in `apps/api/src/services/area.ts` over `SecurityFacts`, one in
 * `adapters/local/area.ts` over `DerivedSecurity` — meant adding a field to
 * `AreaAggregateSchema` required editing both, and editing one would make
 * local and HTTP emit different key sets for the same contract type.
 *
 * The parameter is widened to `GroupMember` because `members` is not read
 * here; `readonly T[]` is covariant, so both call sites still fit.
 */
export function toAreaAggregate(detail: GroupAggregateDetail<GroupMember, AreaId>): AreaAggregate {
  return {
    id: detail.id,
    name: detail.name,
    mcap: detail.mcap,
    ret: detail.ret,
    memberCount: detail.memberCount,
    advancers: detail.advancers,
    decliners: detail.decliners,
  };
}
